import TinyMce from "components/GbCpn/TinyMce";
import SidebarLayout from "components/Layout/Layout";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_URL } from "env";
import { toast } from "react-toastify";
import { Breadcrumb, Button, Form, Select, Skeleton } from "antd";
import { ArrowLeftIcon } from "@phosphor-icons/react";

const EventDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [selectedLang, setSelectedLang] = useState<'en' | 'vi'>('en');
    const [eventType, setEventType] = useState('');
    const [title, setTitle] = useState({ en: '', vi: '' });
    const [subTitle, setSubTitle] = useState({ en: '', vi: '' });
    const [description, setDescription] = useState({ en: '', vi: '' });

    function checkData() {
        for (const lang of ['en', 'vi'] as const) {
            if (!title[lang]) { toast.error(`Title (${lang.toUpperCase()}) is required`); return false; }
            if (!subTitle[lang]) { toast.error(`Sub Title (${lang.toUpperCase()}) is required`); return false; }
            if (!description[lang]) { toast.error(`Description (${lang.toUpperCase()}) is required`); return false; }
        }
        return true;
    }

    async function getContent() {
        if (!id) return;
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}api/v1/be/events/${id}`, {
                method: "GET",
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            });
            const data = await response.json();
            setEventType(data.data.type);
            setTitle({ en: data.data.title.en, vi: data.data.title.vi });
            setSubTitle({ en: data.data.subtitle.en, vi: data.data.subtitle.vi });
            const desc = JSON.parse(data.data.description);
            setDescription({ en: desc.en, vi: desc.vi });
            form.setFieldsValue({ eventType: data.data.type });
        } catch (error) {
            console.error('There was a problem with the fetch operation:', error);
        } finally {
            setLoading(false);
        }
    }

    async function saveContent() {
        if (!id || !checkData()) return;
        setSaving(true);
        try {
            const response = await fetch(`${API_URL}api/v1/be/events/${id}`, {
                method: "PUT",
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                body: JSON.stringify({ title, subtitle: subTitle, description, type: eventType })
            });
            const data = await response.json();
            if (data.status) { toast.success('Event updated successfully'); navigate('/events'); }
            else toast.error(data.message || 'Failed to update event');
        } catch (error) {
            console.error('There was a problem with the fetch operation:', error);
        } finally {
            setSaving(false);
        }
    }

    useEffect(() => { getContent(); }, [id]);

    return (
        <SidebarLayout>
            <div className="space-y-4">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigate('/events')}
                        className="flex items-center justify-center w-7 h-7 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors cursor-pointer"
                    >
                        <ArrowLeftIcon size={16} className="text-white" />
                    </button>
                    <Breadcrumb
                        separator=">"
                        items={[
                            { title: <span className="cursor-pointer" onClick={() => navigate('/events')}>Events</span> },
                            { title: loading ? '...' : `Event #${id}` },
                        ]}
                    />
                </div>

                {loading ? (
                    <div className="bg-bg-card border border-border rounded-xl p-6">
                        <Skeleton active paragraph={{ rows: 6 }} />
                    </div>
                ) : id ? (
                    <div className="bg-bg-card border border-border rounded-xl p-6 space-y-5">
                        {/* Lang + Type row */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-xs font-medium text-text-secondary mb-1.5">Language</p>
                                <Select
                                    value={selectedLang}
                                    onChange={setSelectedLang}
                                    className="w-full"
                                    options={[
                                        { value: 'en', label: 'English' },
                                        { value: 'vi', label: 'Vietnamese' },
                                    ]}
                                />
                            </div>
                            <div>
                                <p className="text-xs font-medium text-text-secondary mb-1.5">Event Type</p>
                                <Select
                                    value={eventType}
                                    onChange={setEventType}
                                    className="w-full"
                                    options={[
                                        { value: 'news', label: 'News' },
                                        { value: 'update', label: 'Update' },
                                        { value: 'announcement', label: 'Announcement' },
                                        { value: 'promotion', label: 'Promotion' },
                                        { value: 'challenge', label: 'Challenge' },
                                        { value: 'other', label: 'Other' },
                                    ]}
                                />
                            </div>
                        </div>

                        {/* Title */}
                        <div>
                            <p className="text-xs font-medium text-text-secondary mb-1.5">Title ({selectedLang.toUpperCase()})</p>
                            <input
                                type="text"
                                className="w-full px-3 py-2 rounded-lg border border-border bg-bg-secondary text-sm focus:outline-none focus:border-blue-400 transition-colors"
                                placeholder="Title"
                                value={selectedLang === 'vi' ? title.vi : title.en}
                                onChange={e => setTitle(prev => ({ ...prev, [selectedLang]: e.target.value }))}
                            />
                        </div>

                        {/* Subtitle */}
                        <div>
                            <p className="text-xs font-medium text-text-secondary mb-1.5">Subtitle ({selectedLang.toUpperCase()})</p>
                            <input
                                type="text"
                                className="w-full px-3 py-2 rounded-lg border border-border bg-bg-secondary text-sm focus:outline-none focus:border-blue-400 transition-colors"
                                placeholder="Subtitle"
                                value={selectedLang === 'vi' ? subTitle.vi : subTitle.en}
                                onChange={e => setSubTitle(prev => ({ ...prev, [selectedLang]: e.target.value }))}
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <p className="text-xs font-medium text-text-secondary mb-1.5">Description ({selectedLang.toUpperCase()})</p>
                            <TinyMce
                                defaultValue={selectedLang === 'vi' ? description.vi : description.en}
                                onChange={content => setDescription(prev => ({ ...prev, [selectedLang]: content }))}
                            />
                        </div>

                        <div className="flex justify-end pt-2 border-t border-border">
                            <Button type="primary" loading={saving} onClick={saveContent}>
                                Save Changes
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="bg-bg-card border border-border rounded-xl p-6">
                        <p className="text-text-muted">No event selected.</p>
                    </div>
                )}
            </div>
        </SidebarLayout>
    );
};

export default EventDetail;
